import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/**
 * Manual add-donor dialog.
 *
 * Runs the payload through the SAME server-side match ladder the CSV importer
 * uses (POST /caller/donors → _DonorMatchIndex). On 409 duplicate response,
 * shows candidates with a primary "Add to <List>" action that joins the
 * existing donor to the chosen list (via POST /caller/donors/{id}/lists) and
 * a de-emphasized secondary "None of these — create a new donor" that
 * re-POSTs with confirm_new=true.
 *
 * Props:
 *   open              boolean
 *   onClose()         close handler
 *   lists             [{id, name, category_type}] — pickable donor-backed lists
 *   defaultCategoryId string — pre-selected list id (list-view integration)
 *   lockCategory      boolean — if true, hide the list picker (list-view case)
 *   onCreated(donor)  called on new-create success with the created donor doc
 *   onJoined({donor_id, category_id, category_name})
 *                     called on "add to existing" success from the 409 branch
 */
export function AddDonorDialog({
  open,
  onClose,
  lists = [],
  defaultCategoryId = "",
  lockCategory = false,
  onCreated = null,
  onJoined = null,
}) {
  const pickable = lists.filter(
    (l) => (l.category_type || "call_list") !== "maxed",
  );

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [employer, setEmployer] = useState("");
  const [occupation, setOccupation] = useState("");
  const [notes, setNotes] = useState("");
  const [categoryId, setCategoryId] = useState(defaultCategoryId || (pickable[0]?.id || ""));
  const [showGiving, setShowGiving] = useState(false);
  const [totalGiven, setTotalGiven] = useState("");
  const [highestGift, setHighestGift] = useState("");
  const [lastGiftAmount, setLastGiftAmount] = useState("");
  const [lastGiftDate, setLastGiftDate] = useState("");
  const [numberOfGifts, setNumberOfGifts] = useState("");
  const [cycleToDate, setCycleToDate] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [duplicateCheck, setDuplicateCheck] = useState(null); // { reason, candidates }
  const [busyCandidateId, setBusyCandidateId] = useState(null);

  useEffect(() => {
    if (open) {
      setDuplicateCheck(null);
      setCategoryId(defaultCategoryId || (pickable[0]?.id || ""));
    }
  }, [open, defaultCategoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const chosenList = pickable.find((l) => l.id === categoryId);
  const chosenListName = chosenList?.name || "the selected list";

  const buildPayload = (confirmNew) => {
    const p = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      city: city.trim(),
      state: state.trim(),
      phone: phone.trim(),
      email: email.trim(),
      employer: employer.trim(),
      occupation: occupation.trim(),
      donor_notes: notes.trim(),
      category_id: categoryId,
      confirm_new: !!confirmNew,
    };
    if (showGiving) {
      const num = (v) => (v === "" || v === null ? 0 : Number(v));
      const intv = (v) => (v === "" || v === null ? 0 : parseInt(v, 10) || 0);
      p.total_given = num(totalGiven);
      p.highest_gift = num(highestGift);
      p.last_gift_amount = num(lastGiftAmount);
      p.last_gift_date = lastGiftDate || null;
      p.number_of_gifts = intv(numberOfGifts);
      p.cycle_to_date = num(cycleToDate);
    }
    return p;
  };

  const resetAndClose = () => {
    setFirstName(""); setLastName(""); setCity(""); setState("");
    setPhone(""); setEmail(""); setEmployer(""); setOccupation("");
    setNotes(""); setShowGiving(false);
    setTotalGiven(""); setHighestGift(""); setLastGiftAmount("");
    setLastGiftDate(""); setNumberOfGifts(""); setCycleToDate("");
    setDuplicateCheck(null);
    setSubmitting(false);
    onClose();
  };

  const submit = async (confirmNew = false) => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("First and last name are required.");
      return;
    }
    if (!categoryId) {
      toast.error("Please pick a list.");
      return;
    }
    setSubmitting(true);
    setDuplicateCheck(null);
    try {
      const { data } = await api.post("/caller/donors", buildPayload(confirmNew));
      toast.success(`Added ${data.donor.first_name} ${data.donor.last_name}.`);
      if (onCreated) onCreated(data.donor);
      resetAndClose();
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data;
      if (status === 409 && body?.duplicate_check === "candidates_found") {
        setDuplicateCheck(body);
      } else {
        toast.error(formatApiErrorDetail(err) || "Couldn't add donor.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const joinExisting = async (candidate) => {
    if (!categoryId) {
      toast.error("Please pick a list first.");
      return;
    }
    setBusyCandidateId(candidate.id);
    try {
      await api.post(`/caller/donors/${candidate.id}/lists`, { category_id: categoryId });
      toast.success(`Added ${candidate.first_name} ${candidate.last_name} to ${chosenListName}.`);
      if (onJoined) onJoined({ donor_id: candidate.id, category_id: categoryId, category_name: chosenListName });
      resetAndClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Couldn't add to list.");
    } finally {
      setBusyCandidateId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) resetAndClose(); }}
      data-testid="add-donor-dialog"
    >
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl max-h-[92vh] overflow-y-auto">
        {!duplicateCheck ? (
          <div className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-flag-navy">Add a donor</h2>
                <p className="text-xs text-slate-500 mt-0.5">Creates a canonical donor record. Uploads for the same person later will match this record.</p>
              </div>
              <button type="button" onClick={resetAndClose} className="text-slate-400 hover:text-slate-700" data-testid="add-donor-close" aria-label="Close">✕</button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="add-donor-first">First name*</Label>
                <Input id="add-donor-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="add-donor-first" />
              </div>
              <div>
                <Label htmlFor="add-donor-last">Last name*</Label>
                <Input id="add-donor-last" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="add-donor-last" />
              </div>
              <div>
                <Label htmlFor="add-donor-city">City</Label>
                <Input id="add-donor-city" value={city} onChange={(e) => setCity(e.target.value)} data-testid="add-donor-city" />
              </div>
              <div>
                <Label htmlFor="add-donor-state">State</Label>
                <Input id="add-donor-state" value={state} onChange={(e) => setState(e.target.value)} data-testid="add-donor-state" maxLength={40} />
              </div>
              <div>
                <Label htmlFor="add-donor-phone">Phone</Label>
                <Input id="add-donor-phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="add-donor-phone" />
              </div>
              <div>
                <Label htmlFor="add-donor-email">Email</Label>
                <Input id="add-donor-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="add-donor-email" />
              </div>
              <div>
                <Label htmlFor="add-donor-employer">Employer</Label>
                <Input id="add-donor-employer" value={employer} onChange={(e) => setEmployer(e.target.value)} data-testid="add-donor-employer" />
              </div>
              <div>
                <Label htmlFor="add-donor-occupation">Occupation</Label>
                <Input id="add-donor-occupation" value={occupation} onChange={(e) => setOccupation(e.target.value)} data-testid="add-donor-occupation" />
              </div>
              {!lockCategory && (
                <div className="col-span-2">
                  <Label htmlFor="add-donor-list">Add to list*</Label>
                  <select
                    id="add-donor-list"
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm"
                    data-testid="add-donor-list"
                  >
                    <option value="">— pick a list —</option>
                    {pickable.map((l) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="col-span-2">
                <Label htmlFor="add-donor-notes">Notes</Label>
                <Textarea id="add-donor-notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="add-donor-notes" rows={2} />
              </div>
            </div>

            {(!city.trim() || !state.trim()) && (
              <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="add-donor-hint-citystate">
                City and state help us recognize this person on future uploads.
              </p>
            )}

            <div className="mt-4 border-t border-slate-200 pt-3">
              <button
                type="button"
                onClick={() => setShowGiving((v) => !v)}
                className="text-xs font-semibold text-flag-navy hover:underline"
                data-testid="add-donor-toggle-giving"
              >
                {showGiving ? "− Hide giving history" : "+ Add giving history (optional)"}
              </button>
              {showGiving && (
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="add-donor-total">Total given ($)</Label>
                    <Input id="add-donor-total" value={totalGiven} onChange={(e) => setTotalGiven(e.target.value)} type="number" min="0" step="0.01" data-testid="add-donor-total-given" />
                  </div>
                  <div>
                    <Label htmlFor="add-donor-highest">Highest gift ($)</Label>
                    <Input id="add-donor-highest" value={highestGift} onChange={(e) => setHighestGift(e.target.value)} type="number" min="0" step="0.01" data-testid="add-donor-highest" />
                  </div>
                  <div>
                    <Label htmlFor="add-donor-last-amt">Last gift ($)</Label>
                    <Input id="add-donor-last-amt" value={lastGiftAmount} onChange={(e) => setLastGiftAmount(e.target.value)} type="number" min="0" step="0.01" data-testid="add-donor-last-amount" />
                  </div>
                  <div>
                    <Label htmlFor="add-donor-last-date">Last gift date</Label>
                    <Input id="add-donor-last-date" value={lastGiftDate} onChange={(e) => setLastGiftDate(e.target.value)} type="date" data-testid="add-donor-last-date" />
                  </div>
                  <div>
                    <Label htmlFor="add-donor-ngifts"># gifts</Label>
                    <Input id="add-donor-ngifts" value={numberOfGifts} onChange={(e) => setNumberOfGifts(e.target.value)} type="number" min="0" step="1" data-testid="add-donor-ngifts" />
                  </div>
                  <div>
                    <Label htmlFor="add-donor-ctd">Cycle-to-date ($)</Label>
                    <Input id="add-donor-ctd" value={cycleToDate} onChange={(e) => setCycleToDate(e.target.value)} type="number" min="0" step="0.01" data-testid="add-donor-ctd" />
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" onClick={resetAndClose} disabled={submitting} data-testid="add-donor-cancel">Cancel</Button>
              <Button type="button" onClick={() => submit(false)} disabled={submitting} data-testid="add-donor-submit">
                {submitting ? "Adding…" : "Add donor"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-5" data-testid="add-donor-duplicate-panel">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-lg font-bold text-flag-navy">
                  {firstName} {lastName} may already be in your donors.
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  {duplicateCheck.reason === "exact" && "Exact match on name + city + state."}
                  {duplicateCheck.reason === "multiple_exact" && "More than one existing donor matches exactly."}
                  {duplicateCheck.reason === "contact_match" && "A donor with a close name shares this email or phone."}
                  {duplicateCheck.reason === "possible" && "Possible match — first name variant, city typo, or missing city."}
                </p>
              </div>
              <button type="button" onClick={resetAndClose} className="text-slate-400 hover:text-slate-700" aria-label="Close">✕</button>
            </div>

            <div className="space-y-2">
              {duplicateCheck.candidates.map((c) => (
                <div
                  key={c.id}
                  className="border border-slate-200 rounded-lg p-3 flex items-start justify-between gap-3"
                  data-testid={`add-donor-candidate-${c.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-flag-navy">{c.first_name} {c.last_name}</div>
                    <div className="text-xs text-slate-600">
                      {[c.city, c.state].filter(Boolean).join(", ") || "no city/state"}
                      {c.phone ? ` · ${c.phone}` : ""}
                      {c.email ? ` · ${c.email}` : ""}
                    </div>
                    {c.lists.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {c.lists.map((l) => (
                          <span key={l.id} className="inline-block text-[10px] font-semibold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                            {l.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => joinExisting(c)}
                    disabled={busyCandidateId === c.id}
                    data-testid={`add-donor-join-${c.id}`}
                    className="shrink-0"
                  >
                    {busyCandidateId === c.id ? "Adding…" : `Add to ${chosenListName}`}
                  </Button>
                </div>
              ))}
            </div>

            <div className="mt-5 flex items-center justify-between border-t border-slate-200 pt-3">
              <button
                type="button"
                onClick={() => setDuplicateCheck(null)}
                className="text-xs font-semibold text-slate-500 hover:text-slate-700"
                data-testid="add-donor-back-to-form"
              >
                ← Back to form
              </button>
              <button
                type="button"
                onClick={() => submit(true)}
                disabled={submitting}
                className="text-xs font-semibold text-slate-400 hover:text-flag-red underline"
                data-testid="add-donor-force-create"
              >
                None of these — create a new donor
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
